import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface Props {
  itemCount:    number
  onStartAudit: () => void
}

const THRESHOLD = 10

export default function PantryAuditCard({ itemCount, onStartAudit }: Props) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('pantry-audit-dismissed') === '1'
  )

  if (dismissed || itemCount >= THRESHOLD || itemCount === 0) return null

  function handleDismiss() {
    localStorage.setItem('pantry-audit-dismissed', '1')
    setDismissed(true)
  }

  const remaining = THRESHOLD - itemCount

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="mx-4 mb-3 rounded-2xl overflow-hidden"
          style={{ border: '1.5px solid rgba(107,158,120,0.3)' }}
        >
          {/* Gradient bar */}
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #6B9E78, #4A90D9)' }} />

          <div className="bg-white p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">🏠</span>
                <div>
                  <p className="text-sm font-semibold text-[#2C2A26]">
                    Stock up your pantry
                  </p>
                  <p className="text-xs text-[#9A9491] mt-0.5">
                    You have {itemCount} item{itemCount !== 1 ? 's' : ''} — add {remaining} more to unlock predictions
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-[#C5BFB8] text-xs shrink-0 hover:text-[#9A9491] transition-colors"
              >
                ×
              </button>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-[#F2EDE6] rounded-full overflow-hidden mb-3">
              <motion.div
                className="h-full rounded-full"
                style={{ background: '#6B9E78' }}
                initial={{ width: 0 }}
                animate={{ width: `${(itemCount / THRESHOLD) * 100}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>

            <p className="text-xs text-[#7A7468] leading-relaxed mb-3">
              Walk around your home and add what you already have. Pantry will
              start predicting your shopping list right away instead of waiting weeks to learn.
            </p>

            <div className="flex gap-2">
              <button
                onClick={onStartAudit}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ background: '#6B9E78' }}
              >
                🏠 Stock my pantry
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 rounded-xl text-sm text-[#7A7468] border border-[#E8E2DC]"
              >
                Later
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
